import { type FC, type JSX, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatNumber } from '@/utils/numberUtils.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import PopoverOrModal from '@/app/common/components/PopoverOrModal';
import { MODAL_BODY_PADDING_UNDER_HEADER } from '@/app/components/search/components/Modal/OpenXModal/utils.ts';
import TripAdvisorBubbles from '@/app/components/hotel-detail/trip-advisor/TripAdvisorBubbles';
import TripAdvisorRatingsPopover from '@/app/components/hotel-detail/trip-advisor/TripAdvisorRatingsPopover';
import TripAdvisorOwl from '@/app/components/hotel-detail/trip-advisor/TripAdvisorOwl';
import TripAdvisorScore from '@/app/components/hotel-detail/trip-advisor/TripAdvisorScore';
import { isCardRatingVisible, isTripAdvisorSource } from '@/app/common/reviews/reviewSource';

import { RatingsContainer, ReviewsNumber, ReviewsTitle, ReviewsTrigger, Score } from './styled';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { decorateHotelRating } from '../../utils/getDecorators';
import TrustYouRatingsPopover from './TrustYouRatingsPopover';

type RatingsProps = {
  dealCard: DealCard;
  isOverlay: boolean;
  hasCertificationBelow?: boolean;
};

const decorateRatings = (rating: number) => {
  const decimalRating = rating / 10;
  if (decimalRating === 10) return decimalRating;
  return decimalRating.toFixed(1);
};

const Ratings: FC<RatingsProps> = ({ dealCard, isOverlay, hasCertificationBelow }): JSX.Element | null => {
  const { localization } = usePageWrapperData();
  const { isDesktop } = useCommonClient();
  const { t } = useTranslation();
  const { locale } = localization;
  const reviewsNum = dealCard?.product?.accommodation?.reviewsNum;
  const rating = dealCard?.product?.accommodation?.rating;
  const stars = dealCard?.product?.accommodation?.stars;
  const tsh = dealCard?.product?.rate?.flags?.tsh;
  const hotelId = dealCard?.product?.accommodation?.hotelId;
  const { reviewsDisabled } = localization.configValuesApp;
  const isTripAdvisor = isTripAdvisorSource(dealCard.reviewSource);
  const showRating = !reviewsDisabled && isCardRatingVisible(rating, dealCard.reviewSource);
  const isMobile = !isDesktop;
  const isClickable = !isOverlay;
  const [triggerEl, setTriggerEl] = useState<HTMLSpanElement | null>(null);

  const hotelReviewClass = decorateHotelRating({
    n: rating,
    excellent: 'excellent',
    verygood: 'very-good',
    good: 'good',
    pleasant: 'pleasant',
    poor: 'poor',
  });
  const hotelReviewTitle = decorateHotelRating({
    n: rating,
    excellent: t('vacation.oss.filters.rating.excellent'),
    verygood: t('vacation.oss.filters.rating.verygood'),
    good: t('vacation.oss.filters.rating.good'),
    pleasant: t('vacation.oss.filters.rating.fair'),
    poor: t('vacation.oss.filters.rating.poor'),
  });
  const hotelReviewsLabel = t('vacation.oss.cards.reviews', {
    amount: `${reviewsNum ? formatNumber({ locale, number: reviewsNum }) : 1}`,
  });
  const hotelReviews = reviewsNum > 0 && !tsh ? `${hotelReviewsLabel}` : '';

  if (!stars && !rating) return null;

  const ratingsTitle = t('vacation.select.details.guest.ratings.title');

  return (
    <RatingsContainer isOverlay={isOverlay} $hasCertificationBelow={hasCertificationBelow}>
      {showRating && (
        <>
          {isTripAdvisor ? (
            <>
              <TripAdvisorScore rawScore={rating} css="margin-right: 8px;" />
              <TripAdvisorOwl alt="TripAdvisor" style={{ marginRight: 8 }} />
              <TripAdvisorBubbles rawScore={rating} css="font-size: 14px; margin-right: 8px;" />
            </>
          ) : (
            <>
              <Score variant="Title6" className={hotelReviewClass}>
                {decorateRatings(rating)}
              </Score>
              {hotelReviewTitle && (
                <ReviewsTitle variant="BodySmall" color="textSecondary">
                  {hotelReviewTitle}&nbsp;
                </ReviewsTitle>
              )}
            </>
          )}
          <ReviewsTrigger
            ref={setTriggerEl}
            {...(isClickable && {
              role: 'button',
              tabIndex: 0,
            })}
          >
            <ReviewsNumber
              variant="BodySmall"
              color={isTripAdvisor ? 'textPrimary' : 'textSecondary'}
              $hasLink={isClickable}
            >
              {hotelReviews ? (
                <>
                  {!isTripAdvisor && '· '}
                  <span>{hotelReviews}</span>
                </>
              ) : (
                ''
              )}
            </ReviewsNumber>
          </ReviewsTrigger>
        </>
      )}

      <PopoverOrModal
        anchorEl={isClickable ? triggerEl : null}
        isMobile={isMobile}
        usePortal={false}
        aria-label={ratingsTitle}
        title={ratingsTitle}
        id={`hotel-reviews-popover-${hotelId}`}
        bodyBackgroundColor="backgroundPage"
        align="left"
        width="304px"
        bodyPadding={MODAL_BODY_PADDING_UNDER_HEADER}
      >
        {isTripAdvisor ? (
          <TripAdvisorRatingsPopover
            rawScore={rating}
            reviewsNum={reviewsNum ?? 0}
            hotelId={hotelId}
            onViewMore={undefined}
          />
        ) : (
          <TrustYouRatingsPopover hotelId={hotelId} language={localization.lang} onViewMore={undefined} />
        )}
      </PopoverOrModal>
    </RatingsContainer>
  );
};

export default Ratings;
